// This app's RTK Query cache policy, applied to @bambi/api-client's
// singleton. api-client stays spec-shaped (one tag per operation, plus the
// overrides in api-client/splits.ts — both coupled the same below); what a
// mutation invalidates beyond its own resource is an app concern, so it
// lives here.
import { bambiApi as specApi } from '@bambi/api-client';
// A split is imported here when one of its endpoints needs policing: a
// coupled tag, an OVERRIDES row, or a mutation whose tag legacy queries
// still provide. developerPlatform and whatTimeIsIt need none, and the
// developer platform screen is lazy-loaded, so it stays out of the bootstrap.
import '@bambi/api-client/assignments';
import '@bambi/api-client/bambi';
import '@bambi/api-client/messageSettings';
import '@bambi/api-client/routes';
import '@bambi/api-client/trips';
import type { UnknownAction } from '@reduxjs/toolkit';

import { applyDispatchDeltaPolicy } from './dispatchDelta';
import { applyNotificationReadPatchPolicy } from './notificationReadPatch';
import {
  COUNT_TAGS,
  type MutationCountTag,
  TRIP_MUTATION_TAGS,
} from './policyTags';
import { enhancedBambiApi as legacyApi } from 'src/common/external/bambi-api/enhancedBambiApi';

type Tags = readonly string[];
type TagPatch = { providesTags?: Tags; invalidatesTags?: Tags };
// What enhanceEndpoints hands a function-form entry: the live definition,
// or undefined when nothing injected that name.
type Lifecycle = {
  dispatch: (action: UnknownAction) => unknown;
  queryFulfilled: Promise<{ data: unknown; meta: unknown }>;
};
type Definition =
  | {
      providesTags?: unknown;
      invalidatesTags?: unknown;
      onQueryStarted?: (arg: unknown, api: Lifecycle) => unknown;
    }
  | undefined;
type Patch = (definition: Definition) => void;

// Only the single-tag lists ['trips'], ['dispatch'], ['run-bambi-run'] are
// widened — a list that already names more is a deliberate override.
const coupleInvalidation: Patch = (definition) => {
  if (!definition) {
    return;
  }
  const current = definition.invalidatesTags;
  if (
    Array.isArray(current) &&
    current.length === 1 &&
    (TRIP_MUTATION_TAGS as Tags).includes(current[0])
  ) {
    definition.invalidatesTags = [...TRIP_MUTATION_TAGS];
  }
};

const alsoInvalidates = (...extra: readonly MutationCountTag[]): TagPatch => ({
  invalidatesTags: [...TRIP_MUTATION_TAGS, ...extra],
});

// Keyed by operationId. Assign/reassign/unassign and their bulk forms are
// not here: their tags depend on the response (dispatchDelta.ts).
const OVERRIDES: Record<string, TagPatch | undefined> = {
  tripsRequestedCountRetrieve: { providesTags: ['requested-trip-count'] },
  tripsActivatedWillCallCountRetrieve: { providesTags: ['will-call-count'] },
  tripsAcceptPartialUpdate: alsoInvalidates('requested-trip-count'),
  tripsBulkAcceptCreate: alsoInvalidates('requested-trip-count'),
  tripsRejectPartialUpdate: alsoInvalidates(
    'requested-trip-count',
    'will-call-count'
  ),
  tripsBulkRejectCreate: alsoInvalidates(
    'requested-trip-count',
    'will-call-count'
  ),
  tripsUnrejectPartialUpdate: alsoInvalidates('requested-trip-count'),
  tripsOverrideStatusPartialUpdate: alsoInvalidates(
    'requested-trip-count',
    'will-call-count'
  ),
  tripsActivateWillCallPartialUpdate: alsoInvalidates('will-call-count'),
  tripsCancelPartialUpdate: alsoInvalidates(
    'requested-trip-count',
    'will-call-count'
  ),
  tripsAcceptCancellationRequestPartialUpdate: alsoInvalidates(
    'requested-trip-count',
    'will-call-count'
  ),
  tripsBulkCancelCreate: alsoInvalidates(
    'requested-trip-count',
    'will-call-count'
  ),
  tripsUncancelPartialUpdate: alsoInvalidates(
    'requested-trip-count',
    'will-call-count'
  ),
  tripsDestroy: alsoInvalidates('requested-trip-count', 'will-call-count'),
  tripsPartialUpdate: alsoInvalidates('will-call-count'),
  tripsUpdatePickupAtPartialUpdate: alsoInvalidates('will-call-count'),
  // Validation and pricing change no data. Invalidating here refetched grids
  // for nothing and raced /dispatch/ against the assign that follows (BAM-793).
  tripsValidatePartialUpdate: { invalidatesTags: [] },
  tripsBulkValidateCreate: { invalidatesTags: [] },
  tripsBulkPriceCreate: { invalidatesTags: [] },
  // A read-once lookup behind the audit-log report (POST-shaped, so the
  // generator makes it a 'trips' query); a trips invalidation re-POSTing
  // the whole id list buys nothing, the report keeps its first answer.
  tripsBulkTripEventsCreate: { providesTags: [] },
  tripsMultiloadCreate: { invalidatesTags: ['dispatch', 'trips'] },
  tripsMultiloadPartialUpdate: { invalidatesTags: ['dispatch', 'trips'] },
  tripsMultiloadDestroy: { invalidatesTags: ['dispatch', 'trips'] },
  // The board does NOT provide 'trips' (bambi-api/README.md, "The tags");
  // 'assignments' stays so crew/break/transfer mutations refetch the halves.
  dispatchAssignedRetrieve: { providesTags: ['dispatch', 'assignments'] },
  dispatchUnassignedRetrieve: { providesTags: ['dispatch', 'assignments'] },
  // Keeps 'dispatch' so mutations that still refetch the board refresh the
  // counts; 'dispatch-summary' is what delta-carrying mutations invalidate.
  dispatchSummaryRetrieve: { providesTags: ['dispatch', 'dispatch-summary'] },
  runBambiRunApplyCreate: alsoInvalidates(
    'requested-trip-count',
    'will-call-count'
  ),
  routesRouteBambiRouteCreate: {
    invalidatesTags: [
      'routes',
      ...TRIP_MUTATION_TAGS,
      'requested-trip-count',
      'will-call-count',
    ],
  },
};

// RTK's own shapes for invalidatesTags: a static list, or a function of
// the settled request.
type TagsDescription =
  | Tags
  | ((result: unknown, error: unknown, arg: unknown, meta: unknown) => Tags);

// Transitional, gone with the legacy client: the two clients have separate
// tag registries, so a mutation on one has to tell the other what it
// invalidated or queries there that provide the tag go stale (an api-client
// assignments mutation and the legacy dispatch halves; a legacy passenger
// delete and the api-client trip grids). Settled = fulfilled or
// rejected-with-value, RTK's own rule; the receiving middleware then scopes
// and defers the refetches exactly as it does for its own mutations.
const mirrorInvalidation =
  (forward: (tags: string[]) => UnknownAction | undefined): Patch =>
  (definition) => {
    if (!definition || definition.invalidatesTags === undefined) {
      return;
    }
    const described = definition.invalidatesTags as TagsDescription;
    const inner = definition.onQueryStarted;
    definition.onQueryStarted = async (arg, api) => {
      void inner?.(arg, api);
      let result: unknown;
      let error: unknown;
      let meta: unknown;
      try {
        ({ data: result, meta } = await api.queryFulfilled);
      } catch (rejection) {
        const settled = rejection as {
          error: unknown;
          isUnhandledError: boolean;
          meta: unknown;
        };
        if (settled.isUnhandledError) {
          return;
        }
        ({ error, meta } = settled);
      }
      const action = forward([
        ...(typeof described === 'function'
          ? described(result, error, arg, meta)
          : described),
      ]);
      if (action) {
        api.dispatch(action);
      }
    };
  };

type LegacyTag = Parameters<typeof legacyApi.util.invalidateTags>[0][number];

// Tags no legacy query provides any more: their legacy split modules are
// gone, so the legacy client no longer registers them and forwarding one
// would only trip RTK's dev-mode tagTypes check. Grows as splits retire.
const RETIRED_LEGACY_TAGS: ReadonlySet<string> = new Set([
  'routes',
  'developer-platform',
  'what-time-is-it',
  'notifications',
]);

const mirrorIntoLegacy = mirrorInvalidation((tags) => {
  const kept = tags.filter((tag) => !RETIRED_LEGACY_TAGS.has(tag));
  return kept.length > 0
    ? legacyApi.util.invalidateTags(kept as LegacyTag[])
    : undefined;
});

// Only covers the splits imported above; a future split emitting a coupled
// single-tag mutation is silently left uncoupled — the throw below guards
// OVERRIDES keys only.
const policyFor =
  (name: string): Patch =>
  (definition) => {
    const override = OVERRIDES[name];
    if (override) {
      // Function form on purpose: the object form silently no-ops when
      // nothing injected `name` (a split import missing above). This throws.
      if (!definition) {
        throw new Error(
          `cachePolicy: ${name} is not an injected endpoint; import its @bambi/api-client split above`
        );
      }
      Object.assign(definition, override);
    } else {
      coupleInvalidation(definition);
    }
    mirrorIntoLegacy(definition);
  };

// Response-dependent tags and their onQueryStarted go on first: the bridge
// above chains whatever handler it finds, and an override would clobber it.
applyDispatchDeltaPolicy();
applyNotificationReadPatchPolicy();

const patches: Record<string, Patch> = Object.fromEntries(
  [
    ...new Set([...Object.keys(specApi.endpoints), ...Object.keys(OVERRIDES)]),
  ].map((name) => [name, policyFor(name)])
);

export const bambiApi = specApi.enhanceEndpoints({
  addTagTypes: COUNT_TAGS,
  endpoints: patches,
});

type ApiClientTag = Parameters<typeof bambiApi.util.invalidateTags>[0][number];

// The other direction. Nothing to prune: api-client registers every spec
// tag, and the count tags were added just above. Covers every endpoint the
// legacy client has injected by import time — the catch-all, which is all
// that is left of it.
const mirrorIntoApiClient = mirrorInvalidation((tags) =>
  bambiApi.util.invalidateTags(tags as ApiClientTag[])
);

legacyApi.enhanceEndpoints({
  endpoints: Object.fromEntries(
    Object.keys(legacyApi.endpoints).map((name) => [name, mirrorIntoApiClient])
  ),
});
